import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row,
  Select,
  Table,
} from 'antd';

import DisplayText from '../../../common/text/DisplayText';
import OnTraccrButton from '../../../common/buttons/OnTraccrButton';
import Colors from '../../../constants/Colors';

import { getCategories } from '../../../costcodes/state/costcodes.actions';
import { getSageIntacctStandardCostTypes } from '../../state/settings.actions';

export default function SageIntacctCostTypeMapping({
  selectedIntegration,
  onIntegrationUpdate,
  isConnected,
}) {
  const dispatch = useDispatch();
  const standardCostTypesByIntegrationId = useSelector(
    (state) => state.settings.sageIntacctStandardCostTypes,
  );
  const standardCostTypes = standardCostTypesByIntegrationId[selectedIntegration?.id] ?? [];
  const categoriesIdMap = useSelector((state) => state.costcodes.categoriesIdMap);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(getCategories());
  }, []);

  const loadCostTypes = useCallback(async () => {
    if (!selectedIntegration?.id) return;
    setLoading(true);
    await dispatch(getSageIntacctStandardCostTypes(selectedIntegration.id));
    setLoading(false);
  }, [selectedIntegration?.id]);

  useEffect(() => {
    const integrationId = selectedIntegration?.id;
    if (
      integrationId
      && isConnected
      && !Object.prototype.hasOwnProperty.call(standardCostTypesByIntegrationId, integrationId)
    ) {
      loadCostTypes();
    }
  }, [
    selectedIntegration?.id,
    isConnected,
    standardCostTypesByIntegrationId,
    loadCostTypes,
  ]);

  const categoryOptions = useMemo(() => (
    Object.values(categoriesIdMap).map(({ id, name }) => ({
      value: id,
      label: name,
    }))
  ), [categoriesIdMap]);

  const mapping = selectedIntegration?.standardCostTypeCategoryMap || {};

  const onCategoryChange = useCallback((standardCostTypeKey, categoryId) => {
    const newMap = { ...mapping };
    if (categoryId == null) {
      delete newMap[standardCostTypeKey];
    } else {
      newMap[standardCostTypeKey] = categoryId;
    }
    onIntegrationUpdate({ standardCostTypeCategoryMap: newMap });
  }, [mapping, onIntegrationUpdate]);

  const columns = useMemo(() => [
    {
      title: 'Sage Intacct Cost Type',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 100,
    },
    {
      title: 'Ontraccr Category',
      key: 'category',
      render: (_, record) => (
        <Select
          allowClear
          placeholder="Select category"
          style={{ width: '100%', minWidth: 160 }}
          options={categoryOptions}
          value={mapping[record.sageId] ?? undefined}
          onChange={(value) => onCategoryChange(record.sageId, value)}
        />
      ),
    },
  ], [categoryOptions, mapping, onCategoryChange]);

  const dataSource = useMemo(() => (
    (standardCostTypes || []).map((costType) => ({
      ...costType,
      key: costType.sageId,
    }))
  ), [standardCostTypes]);

  if (!selectedIntegration?.id) return null;

  return (
    <div style={{ width: '100%', padding: 10 }}>
      <DisplayText
        title="Cost Type Category Mapping"
        style={{ fontFamily: 'roboto-medium', marginBottom: 0 }}
      />
      <DisplayText
        title="Map Sage Intacct standard cost types to Ontraccr cost code categories. Cost codes synced in either direction will use these mappings."
        style={{
          fontFamily: 'roboto-regular',
          color: Colors.ONTRACCR_OPACITY_GRAY,
          marginBottom: 10,
          maxWidth: 420,
        }}
      />
      <Row style={{ marginBottom: 10 }}>
        <OnTraccrButton
          title="Refresh cost types"
          loading={loading}
          disabled={!isConnected}
          onClick={loadCostTypes}
        />
      </Row>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        size="small"
        loading={loading}
        locale={{ emptyText: isConnected ? 'No standard cost types found' : 'Connect Sage Intacct to load cost types' }}
        style={{ maxWidth: 520 }}
      />
    </div>
  );
}

SageIntacctCostTypeMapping.propTypes = {
  selectedIntegration: PropTypes.shape({
    id: PropTypes.string,
    standardCostTypeCategoryMap: PropTypes.objectOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ),
  }),
  onIntegrationUpdate: PropTypes.func.isRequired,
  isConnected: PropTypes.bool,
};

SageIntacctCostTypeMapping.defaultProps = {
  selectedIntegration: {},
  isConnected: false,
};
